import { Box, Grid2 as Grid, Link, Typography, Divider } from "@mui/material";
import { displayContractBasis } from "@/utils/contractBasis";
import { Link as RouterLink } from "react-router-dom";
import { formatDate } from "@/utils/formatters/dateFormatter";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { ReferenceField, TextField, useGetMany } from "react-admin";
import { Policy, ResourceName, Vendor } from "@/types";
import { SectionCard } from "@/components/common/SectionCard";
import Button from "@/components/ui/Button";
import { ButtonStyleType } from "@/types/shared/uiComponents";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";

interface PolicyInfoCardProps {
  policy: Policy;
  /** When true, renders content without the SectionCard wrapper (for use inside another card). */
  embedded?: boolean;
}

/** Renders an array of vendor IDs as comma-separated navigable links */
const VendorLinks = ({ ids, fallback }: { ids?: string[]; fallback?: string }) => {
  const { data: vendors = [] } = useGetMany<Vendor>(
    ResourceName.VENDORS,
    { ids: ids ?? [] },
    { enabled: !!ids?.length }
  );

  if (!ids?.length) return <>{fallback || "-"}</>;

  return (
    <>
      {vendors.map((vendor, i) => (
        <span key={vendor.id}>
          <Link
            component={RouterLink}
            to={`/${ResourceName.VENDORS}/${vendor.id}/show`}
            underline="always"
            variant="body2"
          >
            {vendor.company_name ?? String(vendor.id)}
          </Link>
          {i < vendors.length - 1 && ", "}
        </span>
      ))}
    </>
  );
};

export const PolicyInfoCard = ({ policy, embedded = false }: PolicyInfoCardProps) => {
  if (!policy) return null;

  const action = (
    <Button
      component={RouterLink}
      to={`/policies/${policy.id}/show`}
      variant={ButtonStyleType.Text}
      endIcon={<ArrowRightAltIcon />}
    >
      View Policy
    </Button>
  );

  const content = (
    <Grid container spacing={3}>
      {/* Row 1 */}
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
            Policyholder
          </Typography>
          <Typography variant="body1">
            {policy.client_id_ref ? (
              <ReferenceField
                source="client_id_ref"
                reference={ResourceName.CLIENTS}
                record={policy}
                link="show"
                emptyText="Not found"
              >
                <TextField source="client_legal_name" emptyText="-" />
              </ReferenceField>
            ) : policy.source_data?.policyholder ? (
              policy.source_data?.policyholder
            ) : (
              "-"
            )}
          </Typography>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
            Effective Dates
          </Typography>
          <Typography variant="body1">
            {formatDate(policy.effective_start_date)} — {formatDate(policy.effective_end_date)}
          </Typography>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
            Specific Deductible
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {policy.specific_benefit?.specific_deductible
              ? formatUSD(policy.specific_benefit.specific_deductible)
              : "-"}
          </Typography>
        </Box>
      </Grid>

      {/* Divider between rows */}
      <Grid size={{ xs: 12 }}>
        <Divider />
      </Grid>

      {/* Row 2 */}
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
            Carrier
          </Typography>
          <Typography variant="body1">
            {policy.vendor_id_ref ? (
              <ReferenceField
                source="vendor_id_ref"
                reference={ResourceName.VENDORS}
                record={policy}
                link="show"
                emptyText="Not found"
              >
                <TextField source="company_name" emptyText="-" />
              </ReferenceField>
            ) : policy.source_data?.stop_loss_carrier ? (
              policy.source_data?.stop_loss_carrier
            ) : (
              "-"
            )}
          </Typography>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
            TPA
          </Typography>
          <Typography variant="body1">
            <VendorLinks ids={policy.tpa_vendor_ids} fallback={policy.source_data?.third_party_administrator} />
          </Typography>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
            Contract Basis
          </Typography>
          <Typography variant="body1">{displayContractBasis(policy.contract_basis)}</Typography>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
            Rx Vendor
          </Typography>
          <Typography variant="body1">
            <VendorLinks
              ids={policy.rx_vendor_ids}
              fallback={policy.specific_benefit?.rx_provider || policy.aggregate_benefit?.rx_provider}
            />
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );

  if (embedded) {
    return (
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
          Policy
        </Typography>
        {content}
      </Box>
    );
  }

  return (
    <SectionCard title="Policy Details" subtitle="Key information about the selected policy" action={action}>
      {content}
    </SectionCard>
  );
};
